import { useState, useCallback, useEffect } from 'react';
import type { IFormSchema, IFormStep } from '~/types';

interface UseMultiStepFormOptions {
  schema: IFormSchema;
  initialData?: Record<string, unknown>;
  initialStep?: number;
  onStepChange?: (step: number) => void;
}

interface UseMultiStepFormResult {
  currentStep: number;
  currentStepConfig: IFormStep;
  totalSteps: number;
  formData: Record<string, unknown>;
  stepErrors: Record<string, string>;
  isFirstStep: boolean;
  isLastStep: boolean;
  goToNextStep: () => boolean;
  goToPreviousStep: () => void;
  goToStep: (step: number) => void;
  validateCurrentStep: () => boolean;
  updateFormData: (data: Record<string, unknown>) => void;
  getProgress: () => number;
}

export const useMultiStepForm = ({
  schema,
  initialData = {},
  initialStep = 0,
  onStepChange,
}: UseMultiStepFormOptions): UseMultiStepFormResult => {
  const steps = schema.steps || [];
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

  const totalSteps = steps.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const currentStepConfig = steps[currentStep];

  // Notify parent when step changes
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);

  // Validate all fields in the current step
  const validateCurrentStep = useCallback((): boolean => {
    if (!currentStepConfig) return true;

    const errors: Record<string, string> = {};
    const requiredFields = schema.required || [];
    const stepFields = currentStepConfig.fields;

    for (const fieldName of stepFields) {
      const isRequired = requiredFields.includes(fieldName);
      const value = formData[fieldName];

      if (isRequired && (value === undefined || value === null || value === '')) {
        const fieldSchema = schema.properties[fieldName] as Record<string, unknown> | undefined;
        const fieldTitle = (fieldSchema?.title as string) || fieldName;
        errors[fieldName] = `${fieldTitle} is required`;
      }
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  }, [currentStepConfig, formData, schema.properties, schema.required]);

  const goToNextStep = useCallback((): boolean => {
    if (isLastStep) return false;

    const isValid = validateCurrentStep();
    if (!isValid) return false;

    setCurrentStep((prev) => prev + 1);
    setStepErrors({});
    return true;
  }, [isLastStep, validateCurrentStep]);

  const goToPreviousStep = useCallback((): void => {
    if (isFirstStep) return;
    setCurrentStep((prev) => prev - 1);
    setStepErrors({});
  }, [isFirstStep]);

  const goToStep = useCallback(
    (step: number): void => {
      if (step < 0 || step >= totalSteps) return;
      setCurrentStep(step);
      setStepErrors({});
    },
    [totalSteps],
  );

  const updateFormData = useCallback((data: Record<string, unknown>): void => {
    setFormData((prev) => ({ ...prev, ...data }));
    // Clear validation errors when user edits — errors only reappear on Continue/Submit click
    setStepErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return {};
    });
  }, []);

  const getProgress = useCallback((): number => {
    if (totalSteps === 0) return 0;
    return ((currentStep + 1) / totalSteps) * 100;
  }, [currentStep, totalSteps]);

  return {
    currentStep,
    currentStepConfig,
    totalSteps,
    formData,
    stepErrors,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    validateCurrentStep,
    updateFormData,
    getProgress,
  };
};
